/**
 * RP4 — Composite hook combining the SceneContent useEffects.
 *
 * Wraps the bulk-load + standalone layer-state + selection sync +
 * hover bridge effects so the SceneContent body becomes a thin shell.
 *
 * # Why a single hook
 *
 * Each effect was independent but they all read the same registries
 * and the same `invalidate`. Bundling them lets the orchestrator pass
 * the registries object once instead of inflating each useEffect dep
 * list with raw refs.
 *
 * # Opt 8 — Map consolidation (Phase 2)
 *
 * Pre-consolidation the args bundle carried eight parallel ref Maps;
 * now it carries two — `bucketsRef` and `entityRendersRef`. Each
 * downstream effect reads/writes through the consolidated shape.
 */

import { useEffect, type MutableRefObject } from "react";
import type * as THREE from "three";

import type { RendererHandle } from "../types";
import type { BatchedLayer, MeshKeyId } from "../batchedLayer";
import type { AdapterContext } from "../adapters";
import type { LayerVisibilityState } from "../state/layerVisibility";
import type { VisibleSetCache } from "../state/visibleSetCache";
import type {
  CameraSnapshot,
  VisibleEntityGroups,
} from "../lod";
import type { CameraStateSnapshot } from "../cameraApi";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { TypedEntityResolver } from "../adapters/typedFirstRenderable";
import type { BlockDefinition, BlockRecord, DimensionStyle, SavedView } from "@dxf-ai-editor/drawing-model";

import { runSceneBulkLoad } from "./SceneBulkLoad";
import { applyAndMirrorLayerVisibility } from "./LayerVisibilityEffect";
import {
  applySelectionToTextures,
  subscribeHoverHighlight,
} from "./SelectionTextureSync";
import type { BucketState, EntityRender } from "./SceneRefsTypes";
import type { CameraFitDocState } from "./sceneBulkLoadHelpers";

interface UseSceneEffectsArgs {
  // R3F context (re-read from useThree at the call site).
  scene: THREE.Scene;
  gl: THREE.WebGLRenderer;
  invalidate: () => void;
  size: { width: number; height: number };
  camera: THREE.Camera;

  // SceneContent props (from outer Scene3DStage).
  entities: Scene3DStageProps["entities"];
  modelSpaceEntities: Scene3DStageProps["modelSpaceEntities"];
  activeSpaceKind: string;
  /**
   * fix/cache-direct-paper-space — the DISAMBIGUATED active space id
   * (`state.drawing.activeSpaceId`), forwarded into the bulk-load inputs AND
   * into the effect's dep array. It is the only dep that moves when the user
   * switches between two LAYOUT tabs: `activeSpaceKind` is `"layout"` for both,
   * and a streamed document's `entities` is the stable EMPTY sentinel, so
   * without it the pass kept rendering the previously active layout's stubs.
   * See `SceneBulkLoadInputs.activeSpaceId`.
   */
  activeSpaceId: string;
  blockDefinitions: Scene3DStageProps["blockDefinitions"];
  cannoscaleName: Scene3DStageProps["cannoscaleName"];
  annotationScales: Scene3DStageProps["annotationScales"];
  /** PR-LT3 — drawing LTYPE table for custom-linetype dash resolution. */
  linetypes: Scene3DStageProps["linetypes"];
  /**
   * PR-LT3 variant A (A2) — GLOBAL dash-scale sysvars (LTSCALE / MSLTSCALE /
   * PSLTSCALE), forwarded into the bulk-load inputs + published into the
   * relift ref. Optional; absent → all-1 (pre-A2 behaviour).
   */
  linetypeSysvars: Scene3DStageProps["linetypeSysvars"];
  /**
   * @codex #334 (P2-B) — STYLE table (name + fontName), forwarded into the
   * bulk-load inputs so a generically-named STYLE dispatches text on its
   * actual font file. Optional; absent/empty → dispatch on styleName.
   */
  textStyles: Scene3DStageProps["textStyles"];
  selectedEntityIds: Scene3DStageProps["selectedEntityIds"];
  /**
   * fix/selection-highlight-not-applied — live snapshot of the SAME
   * `selectedEntityIds` value, forwarded into `runSceneBulkLoad` so the pass can
   * re-derive the selection byte onto the `SelectionStateTexture`s it just
   * rebuilt (see `restampHighlightState`). Deliberately NOT part of the
   * bulk-load effect's dep array: a selection change must not tear down and
   * rebuild every bucket, it only has to be VISIBLE to a pass that runs for some
   * other reason. Owned by `SceneContent` (which also hands it to `FrameLoops`
   * for the post-relift re-stamp).
   */
  selectedEntityIdsRef: MutableRefObject<readonly string[]>;

  // Layer table (subscribed via useEditorStore in caller).
  layerStates: ReadonlyArray<LayerVisibilityState>;
  geometryDocument: {
    /** Issue #225 — document identity used to gate the per-document auto-fit. */
    id: string;
    styleTables?: { dimensionStyles?: ReadonlyArray<DimensionStyle> };
    blockRecords?: Record<string, BlockRecord>;
    blocks?: Record<string, BlockDefinition>;
    savedView?: SavedView;
  };

  // Lookup helpers + adapter context base.
  buildPreview: (id: string) => EntityPreview | null;
  /** DEFUSE S11 — typed resolver for the typed-first lift (see
   *  `SceneBulkLoadCallbacks.resolveEntity`). */
  resolveEntity: TypedEntityResolver;
  adapterCtxBase: AdapterContext;

  /**
   * Codex P1 #2 (PR #87) — Zustand `entityMetadataIndexVersion` snapshot.
   * Bumped exactly once at end of import (and on subsequent
   * commitMutation calls). Added to the bulk-load effect dep array so
   * the streaming-import path (which keeps `state.drawing.entities`
   * empty and thus `entities` stable as `EMPTY_ENTITIES_IN_SPACE`) still
   * triggers a fresh bulk-load when the index gets populated.
   */
  entityMetadataIndexVersion: number;

  // Refs owned by SceneContent.
  handleRef: MutableRefObject<RendererHandle | null>;
  batchedLayerRef: MutableRefObject<BatchedLayer | null>;
  visibleSetCacheRef: MutableRefObject<VisibleSetCache | null>;
  /**
   * Opt 8 — consolidated per-bucket render state Map.
   */
  bucketsRef: MutableRefObject<Map<MeshKeyId, BucketState>>;
  /**
   * Opt 8 — consolidated per-entity render state Map.
   */
  entityRendersRef: MutableRefObject<Map<string, EntityRender>>;
  textBuildTokenRef: MutableRefObject<number>;
  lockedLayersRef: MutableRefObject<Set<string>>;
  hiddenLayersRef: MutableRefObject<Set<string>>;
  sharedLockedLayersRef: MutableRefObject<Set<string>>;
  prevCameraSnapshotRef: MutableRefObject<CameraSnapshot | null>;
  lodPointsCloudRef: MutableRefObject<THREE.Points | null>;
  /** Codex PR #784 round-12 P2 — attributed viewport point-proxy clouds. */
  lodPointProxyCloudsRef: MutableRefObject<THREE.Points[]>;
  lodGroupsRef: MutableRefObject<VisibleEntityGroups>;
  cameraStateRef: MutableRefObject<CameraStateSnapshot>;
  /**
   * Issue #225 + #235 follow-up (@codex P2 ×2) — per-document auto-fit
   * claim forwarded to `runSceneBulkLoad`. Records the
   * `geometryDocument.id` the most recent bulk-load pass rendered (+
   * whether that document has shown renderable content yet) so a NEW
   * document loaded in the same session re-fits and a streamed
   * document's one fit survives its 0-renderable pre-pass. See
   * `CameraFitDocState` (sceneBulkLoadHelpers).
   */
  cameraFitDocStateRef: MutableRefObject<CameraFitDocState | null>;
  /**
   * Phase 2 Wave 2 Task 4 — InstancedMesh tracker for batched INSERTs.
   */
  instancedBlockMeshesRef: MutableRefObject<Array<THREE.InstancedMesh>>;
  /**
   * Codex P3 follow-up / Item C (2026-05-19) — per-frame viewport-cull
   * re-lift tracking refs forwarded to `runSceneBulkLoad`.
   */
  liftedEntityIdsRef: MutableRefObject<Set<string>>;
  /**
   * S9b — parent MLEADER id → block-content flattened children ref forwarded
   * to `runSceneBulkLoad` (populated there) and read/mutated by FrameLoops'
   * relift path so an MLEADER's render-only block content is added / removed in
   * lockstep with the parent (the children are absent from the spatial index).
   */
  multileaderBlockChildrenRef: MutableRefObject<
    Map<string, import("../types").RenderableEntity[]>
  >;
  /**
   * Codex PR #784 round 3 P1 #2 — revision of `multileaderBlockChildrenRef`,
   * forwarded to `runSceneBulkLoad` which bumps it on clear / repopulate.
   */
  renderSatellitesVersionRef: MutableRefObject<number>;
  entityResolverRef: MutableRefObject<
    | ((
        id: string,
        request?: import("./SceneRefsTypes").EntityLiftRequest,
      ) => import("../types").RenderableEntity | null)
    | null
  >;
  /**
   * FIX-3 (Codex P2) — per-pass LTYPE resolution map ref forwarded to
   * `runSceneBulkLoad` (published there) and read by FrameLoops' relift
   * path to build a new linetype bucket key.
   */
  linetypeResolutionRef: MutableRefObject<
    import("../rendering/linetypeResolve").LinetypeResolutionMap
  >;
  /**
   * FIX-1 (Codex P2) — per-pass per-layer LTYPE map ref forwarded to
   * `runSceneBulkLoad` (published there) and read by FrameLoops' relift path
   * so a `ByLayer` bucket resolves through the layer's assigned linetype.
   */
  layerLinetypeMapRef: MutableRefObject<
    import("../rendering/linetypeResolve").LayerLinetypeMap
  >;
  /**
   * PR-LT3 variant A (A2) — per-pass GLOBAL dash-scale sysvars ref forwarded to
   * `runSceneBulkLoad` (published there) and read by FrameLoops' relift path.
   */
  linetypeSysvarsRef: MutableRefObject<
    import("./useSceneContentRefs").LinetypeSysvarsRefValue
  >;
}

/**
 * Compose SceneContent's four useEffects:
 *
 *   1. **bulkLoad** — entity → renderable → mesh pipeline.
 *   2. **layer visibility** — re-applies on layer-table mutation.
 *   3. **selection sync** — diffs selectedEntityIds onto textures.
 *   4. **hover bridge** — subscribes to the standalone hover store.
 */
export function useSceneEffects(args: UseSceneEffectsArgs): void {
  // 1. Bulk-load. args.* refs are stable per render; mutating .current
  // does not need to re-run the effect.
  //
  // Phase 1.e (П35Р) — revert Phase 1.d setTimeout(0) defer. Live
  // smoke on PR #79 showed `canvasCount=0, stageRenderer=null` after
  // import completion: the defer-then-cancel cascade during rapid
  // dep changes ended up disposing the scene without a final paint,
  // so the Three.js canvas never re-mounted with the imported
  // entities. The import speed-up came from Phase 1.c skip-merge,
  // not the defer (496s → 289s reduction lost only ~10s without the
  // defer). Restoring the sync useEffect path keeps the scene
  // mounted and lets the import settle visible on screen, at the
  // cost of a brief blocking sync paint that the deferred-bulk-load
  // optimization will revisit in a focused PR.
  useEffect(() => {
    return runSceneBulkLoad(
      {
        scene: args.scene,
        gl: args.gl,
        invalidate: args.invalidate,
        size: args.size,
        camera: args.camera,
      },
      {
        entities: args.entities,
        modelSpaceEntities: args.modelSpaceEntities,
        activeSpaceKind: args.activeSpaceKind,
        activeSpaceId: args.activeSpaceId,
        blockDefinitions: args.blockDefinitions,
        cannoscaleName: args.cannoscaleName,
        annotationScales: args.annotationScales,
        linetypes: args.linetypes,
        linetypeSysvars: args.linetypeSysvars,
        textStyles: args.textStyles,
        layerStates: args.layerStates,
      },
      {
        handleRef: args.handleRef,
        batchedLayerRef: args.batchedLayerRef,
        visibleSetCacheRef: args.visibleSetCacheRef,
        bucketsRef: args.bucketsRef,
        entityRendersRef: args.entityRendersRef,
        textBuildTokenRef: args.textBuildTokenRef,
        lockedLayersRef: args.lockedLayersRef,
        hiddenLayersRef: args.hiddenLayersRef,
        sharedLockedLayersRef: args.sharedLockedLayersRef,
        prevCameraSnapshotRef: args.prevCameraSnapshotRef,
        lodPointsCloudRef: args.lodPointsCloudRef,
        lodPointProxyCloudsRef: args.lodPointProxyCloudsRef,
        lodGroupsRef: args.lodGroupsRef,
        cameraStateRef: args.cameraStateRef,
        cameraFitDocStateRef: args.cameraFitDocStateRef,
        instancedBlockMeshesRef: args.instancedBlockMeshesRef,
        liftedEntityIdsRef: args.liftedEntityIdsRef,
        multileaderBlockChildrenRef: args.multileaderBlockChildrenRef,
        renderSatellitesVersionRef: args.renderSatellitesVersionRef,
        entityResolverRef: args.entityResolverRef,
        linetypeResolutionRef: args.linetypeResolutionRef,
        layerLinetypeMapRef: args.layerLinetypeMapRef,
        linetypeSysvarsRef: args.linetypeSysvarsRef,
        selectedEntityIdsRef: args.selectedEntityIdsRef,
      },
      {
        buildPreview: args.buildPreview,
        resolveEntity: args.resolveEntity,
        adapterCtxBase: args.adapterCtxBase,
        geometryDocument: args.geometryDocument,
      },
    );
  }, [
    args.entities,
    args.modelSpaceEntities,
    args.activeSpaceKind,
    // fix/cache-direct-paper-space — a layout→layout switch moves ONLY this
    // dep (see `UseSceneEffectsArgs.activeSpaceId`).
    args.activeSpaceId,
    args.scene,
    args.gl,
    args.invalidate,
    args.size,
    args.camera,
    args.adapterCtxBase,
    args.handleRef,
    args.batchedLayerRef,
    args.blockDefinitions,
    args.cannoscaleName,
    args.annotationScales,
    args.linetypes,
    args.linetypeSysvars,
    args.textStyles,
    args.layerStates,
    args.sharedLockedLayersRef,
    args.cameraStateRef,
    args.cameraFitDocStateRef,
    args.geometryDocument,
    args.buildPreview,
    args.resolveEntity,
    // Codex P1 #2 (PR #87) — see `entityMetadataIndexVersion` JSDoc on
    // `UseSceneEffectsArgs`. Forces the bulk-load effect to re-run
    // after a streaming import bumps the index, even though every
    // other dep (`entities` is the EMPTY sentinel, `geometryDocument`
    // identity stable) is unchanged.
    args.entityMetadataIndexVersion,
  ]);

  // 2. Layer visibility — standalone re-apply on layer-table mutation.
  // F4 (codex P1 #4) — also pass the InstancedMesh tracker so layer
  // toggles (hide / freeze / thaw) propagate to batched-INSERT meshes
  // alongside the bucket handles. Without this, hiding a layer
  // containing batched INSERTs left the meshes painted until the next
  // full bulkLoad.
  useEffect(() => {
    const applied = applyAndMirrorLayerVisibility({
      buckets: args.bucketsRef.current,
      entityRenders: args.entityRendersRef.current,
      instancedBlockMeshes: args.instancedBlockMeshesRef.current,
      layerStates: args.layerStates,
      lockedLayersRef: args.lockedLayersRef,
      hiddenLayersRef: args.hiddenLayersRef,
      sharedLockedLayersRef: args.sharedLockedLayersRef,
    });
    if (applied) args.invalidate();
  }, [
    args.layerStates,
    args.invalidate,
    args.sharedLockedLayersRef,
    args.bucketsRef,
    args.lockedLayersRef,
    args.hiddenLayersRef,
    args.instancedBlockMeshesRef,
  ]);

  // 3. Selection sync.
  useEffect(() => {
    const applied = applySelectionToTextures({
      entityRenders: args.entityRendersRef.current,
      buckets: args.bucketsRef.current,
      selectedEntityIds: args.selectedEntityIds,
    });
    if (applied) args.invalidate();
  }, [
    args.selectedEntityIds,
    args.invalidate,
    args.entityRendersRef,
    args.bucketsRef,
  ]);

  // 4. Hover bridge — subscribe whenever the args bundle changes.
  useEffect(() => {
    return subscribeHoverHighlight({
      entityRenders: args.entityRendersRef.current,
      buckets: args.bucketsRef.current,
      invalidate: args.invalidate,
    });
  }, [
    args.invalidate,
    args.entityRendersRef,
    args.bucketsRef,
  ]);
}
